import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FocusModeContextType {
  isFocusMode: boolean;
  enableFocusMode: () => void;
  disableFocusMode: () => void;
  toggleFocusMode: () => void;
}

const FocusModeContext = createContext<FocusModeContextType | undefined>(undefined);

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
};

interface FocusModeProviderProps {
  children: ReactNode;
}

const FOCUS_MODE_KEY = 'novari_focus_mode';

export const FocusModeProvider: React.FC<FocusModeProviderProps> = ({ children }) => {
  const [isFocusMode, setIsFocusMode] = useState(() => {
    const stored = localStorage.getItem(FOCUS_MODE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(FOCUS_MODE_KEY, String(isFocusMode));
  }, [isFocusMode]);

  const enableFocusMode = () => setIsFocusMode(true);
  const disableFocusMode = () => setIsFocusMode(false);
  const toggleFocusMode = () => setIsFocusMode(prev => !prev);

  return (
    <FocusModeContext.Provider value={{
      isFocusMode,
      enableFocusMode,
      disableFocusMode,
      toggleFocusMode,
    }}>
      {children}
    </FocusModeContext.Provider>
  );
};
